import React, { useState } from "react";
import { Biohazard, ShieldAlert } from "lucide-react";
import { SporePage, sporeBtn } from "./SporeShell";
import { FullscreenButton } from "./FullscreenButton";

const BOOT = [
  { text: "Verifying Level-5 clearance", color: "bg-cyan-400" },
  { text: "Decrypting 31 sealed case files", color: "bg-amber-400" },
  { text: "Locating specialists on rotation", color: "bg-[#FF2E63]" },
];

const Boot = () => (
  <SporePage>
    <div className="relative z-10 min-h-screen flex flex-col items-center justify-center gap-8 px-6 text-center" data-testid="login-boot-screen">
      <Biohazard className="h-20 w-20 text-[#FF2E63] spore-spin-slow drop-shadow-[0_0_20px_rgba(255,46,99,0.6)]" />
      <p className="font-display text-3xl sm:text-4xl font-bold tracking-tight">PROJECT SP<span className="text-[#FF2E63]">-ORE</span></p>
      <ul className="space-y-3 font-tac text-sm text-slate-300">
        {BOOT.map((b, i) => (
          <li key={b.text} className="flex items-center gap-3 spore-fade-up" style={{ animationDelay: `${i * 600}ms` }}>
            <span className={`w-2 h-2 rounded-full ${b.color} animate-ping`} /> {b.text}…
          </li>
        ))}
      </ul>
      <div className="w-72 h-1.5 bg-slate-900 border border-slate-800 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-cyan-500 via-amber-500 to-[#FF2E63] spore-fade-up" style={{ width: "100%", animationDuration: "2.6s" }} />
      </div>
      <p className="font-tac text-xs tracking-[0.3em] text-emerald-300 spore-fade-up" style={{ animationDelay: "2s" }}>ACCESS GRANTED</p>
    </div>
  </SporePage>
);

const Field = ({ label, testid, ...props }) => (
  <label className="block space-y-2">
    <span className="font-tac text-[11px] tracking-[0.25em] text-slate-400 uppercase">{label}</span>
    <input
      data-testid={testid}
      {...props}
      className="w-full bg-slate-950 border border-slate-800 rounded-sm px-3 py-3 text-slate-100 placeholder:text-slate-600 font-tac focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-[border-color] duration-200"
    />
  </label>
);

const Login = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [booting, setBooting] = useState(false);

  const handleLogin = (e) => {
    e.preventDefault();
    setBooting(true);
    setTimeout(() => onLogin(credentials.username || "Medical Officer"), 3000);
  };

  if (booting) return <Boot />;

  return (
    <SporePage>
      <div className="fixed top-4 right-4 z-50">
        <FullscreenButton />
      </div>
      <div className="relative z-10 min-h-screen grid lg:grid-cols-2">
        <div className="hidden lg:flex flex-col justify-between p-12 border-r border-slate-800 spore-grid-bg">
          <p className="font-tac text-xs tracking-[0.3em] text-red-300 flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> AUTHORISED PERSONNEL ONLY</p>
          <div className="space-y-6">
            <Biohazard className="h-16 w-16 text-[#FF2E63] spore-spin-slow" />
            <h1 className="font-display uppercase font-bold text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              31 scientists.<br />31 symptoms.<br /><span className="text-[#FF2E63] spore-glow-red">Infected or not?</span>
            </h1>
            <p className="text-slate-400 max-w-md">Special Projects — Operational Risk and Environment. Every specialist will only stay for one test. Reveal their symptom before they move on, and learn whether it means infection.</p>
          </div>
          <p className="font-tac text-xs text-slate-600">PROJECT SP-ORE OVERSIGHT // HAZARD CLASS: LETHAL BIOGENIC</p>
        </div>
        <div className="flex items-center justify-center p-6">
          <form onSubmit={handleLogin} data-testid="login-form" className="w-full max-w-md rounded-sm border border-slate-800 bg-[#121824] p-8 space-y-6 spore-fade-up">
            <div>
              <p className="font-display text-2xl font-bold">PROJECT SP<span className="text-[#FF2E63]">-ORE</span></p>
              <p className="font-tac text-xs tracking-[0.25em] text-slate-500 mt-1">ACCESS PORTAL // LEVEL-5</p>
            </div>
            <Field label="Officer ID" testid="login-username-input" type="text" placeholder="Enter your ID (or leave blank)" value={credentials.username} onChange={(e) => setCredentials({ ...credentials, username: e.target.value })} />
            <Field label="Access code" testid="login-password-input" type="password" placeholder="Optional" value={credentials.password} onChange={(e) => setCredentials({ ...credentials, password: e.target.value })} />
            <button type="submit" data-testid="login-submit-btn" className={`${sporeBtn.primary} w-full py-3.5`}>
              Enter containment network
            </button>
            <p className="font-tac text-[11px] text-slate-500 text-center">Unauthorised access is punishable by immediate quarantine.</p>
          </form>
        </div>
      </div>
    </SporePage>
  );
};

export default Login;
